import { createSessionStore, type AddressLookupSource } from '@pos/admin-ui';
import { createAddressSource, createHoApi } from '@pos/api-client';
import { currentLanguage } from '@pos/i18n';

export const TENANT_CODE = import.meta.env.VITE_TENANT_CODE ?? 'DEV';

export const useSession = createSessionStore('pos.ho-admin.session');

export const hoApi = createHoApi({
  getAccessToken: () => useSession.getState().session?.accessToken,
  onUnauthorized: () => useSession.getState().end(),
  getLanguage: currentLanguage,
});

/** The location master data the address forms pick from (ADR 0026). */
export const addressSource = createAddressSource(hoApi);

/**
 * The latitude and longitude of an address through HO's maps provider (`Maps:Provider`): Google address suggestions as staff
 * type and the one picked, or the typed text geocoded, for address forms.
 * Offered only when the API says a provider is set up.
 */
export const addressLookup: AddressLookupSource = {
  suggest: async (input, sessionToken) => {
    const { data, error } = await hoApi.POST('/api/v1/locations/addresses/suggest', { body: { input, sessionToken } });
    if (!data) throw error;
    return data;
  },
  resolve: async (placeId, sessionToken) => {
    const { data, error } = await hoApi.POST('/api/v1/locations/addresses/resolve', { body: { placeId, sessionToken } });
    if (!data) throw error;
    return { ...data, latitude: Number(data.latitude), longitude: Number(data.longitude) };
  },
  find: async (address) => {
    const { data, error } = await hoApi.POST('/api/v1/locations/addresses/geocode', { body: { address } });
    if (!data) throw error;
    return data.map((a) => ({ ...a, latitude: Number(a.latitude), longitude: Number(a.longitude) }));
  },
};

export async function isAddressLookupEnabled(): Promise<boolean> {
  const { data } = await hoApi.GET('/api/v1/locations/addresses/geocode-enabled');
  return Boolean((data as { enabled?: boolean } | undefined)?.enabled);
}
